/**
 * Installation de l'application, et ce que chaque plateforme en permet.
 *
 * Trois familles, et une seule commande dans l'interface :
 *
 *  - **Chrome, Edge, et les navigateurs à moteur Chromium** émettent
 *    `beforeinstallprompt`. L'événement est retenu et rejoué au moment où
 *    l'utilisateur appuie : c'est la boîte du navigateur qui s'ouvre, et sur
 *    Android c'est elle qui fait fabriquer un WebAPK — une vraie application
 *    dans le tiroir, et non le raccourci que pose « Ajouter à l'écran
 *    d'accueil ».
 *  - **Safari, iPhone et iPad**, n'a pas d'équivalent : l'installation passe
 *    par la feuille de partage du système, qui se décrit mais ne s'ouvre pas.
 *    D'où le mode `guide`, qui n'affiche qu'un mode d'emploi.
 *  - **Partout ailleurs** — Firefox de bureau, page en cadre, mode kiosque —
 *    rien n'est proposé. Firefox Android sait installer, mais par son propre
 *    menu et sans prévenir la page : un bouton y mènerait à une boîte qui ne
 *    s'ouvre jamais.
 *
 * L'abonnement est posé à l'évaluation du module, et non au démarrage de
 * l'application : `beforeinstallprompt` part dès que le navigateur a jugé la
 * page installable, souvent avant que le tiroir n'existe. Un module importé
 * par la chaîne de `main.ts` est évalué avant que `boot()` ne s'exécute, ce
 * qui suffit à ne rien manquer.
 */

import { canInstallForFullscreen } from './fullscreen'

/**
 * L'événement de Chromium, absent des définitions du DOM.
 *
 * Il n'est pas normalisé — seul Chromium l'implémente —, d'où la déclaration
 * locale plutôt qu'une dépendance de types.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/** Ce que la commande d'installation peut faire, ici et maintenant. */
export type InstallMode = 'prompt' | 'guide' | 'none'

/**
 * Le choix, isolé de toute lecture du document.
 *
 * `installed` prime sur le reste : un navigateur n'émet en principe pas
 * `beforeinstallprompt` pour une application déjà posée, mais l'ordre est
 * écrit plutôt que supposé — c'est la branche qui se casserait en silence.
 */
export function chooseInstallMode(state: {
  promptAvailable: boolean
  appleTab: boolean
  installed: boolean
}): InstallMode {
  if (state.installed) return 'none'
  if (state.promptAvailable) return 'prompt'
  if (state.appleTab) return 'guide'
  return 'none'
}

/**
 * L'application tourne-t-elle déjà comme une application installée ?
 *
 * **`display-mode: fullscreen` n'est délibérément pas interrogé**, alors que
 * `fullscreen.ts` le fait de son côté. Ce mode ne dit pas seulement « installée
 * en plein écran » : il correspond aussi à une page ordinaire passée en plein
 * écran par l'API du navigateur — c'est-à-dire à ce que fait le bouton voisin
 * dans ce même en-tête. L'interroger ici ferait disparaître la commande
 * d'installation d'un simple onglet dès qu'on prend le plein écran, et
 * réapparaître en le rendant.
 *
 * Le manifeste déclare `standalone` : une instance installée se reconnaît donc
 * à ce mode-là, et à `navigator.standalone` sur les appareils d'Apple.
 */
function isInstalled(): boolean {
  if (typeof matchMedia !== 'function') return false

  return (
    matchMedia('(display-mode: standalone)').matches ||
    matchMedia('(display-mode: minimal-ui)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  )
}

let deferred: BeforeInstallPromptEvent | null = null
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    /*
     * `preventDefault` retire la bannière que Chrome Android glisse en bas de
     * l'écran, et rien d'autre : l'entrée « Installer l'application » de son
     * menu reste là. On échange donc une invitation subie contre un bouton
     * que l'utilisateur trouve où il l'attend, dans l'en-tête du tiroir.
     *
     * Sans cet appel, Chrome considère l'événement consommé par sa propre
     * bannière et `prompt()` échouerait plus tard.
     */
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    notify()
  })

  // L'installation peut aussi partir du menu du navigateur : la commande doit
  // disparaître dans ce cas aussi, sans quoi elle ouvrirait une boîte morte.
  window.addEventListener('appinstalled', () => {
    deferred = null
    notify()
  })
}

/** Ce que la commande d'installation peut faire sur cet appareil. */
export function installMode(): InstallMode {
  return chooseInstallMode({
    promptAvailable: deferred !== null,
    appleTab: canInstallForFullscreen(),
    installed: isInstalled(),
  })
}

/**
 * Ouvre la boîte d'installation du navigateur.
 *
 * L'événement retenu ne sert qu'une fois : il est relâché quel que soit le
 * choix de l'utilisateur. Chromium en émet un nouveau plus tard s'il juge la
 * proposition encore pertinente, et l'abonnement le récupérera.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred
  if (!event) return 'unavailable'

  deferred = null

  try {
    await event.prompt()
    const { outcome } = await event.userChoice
    return outcome
  } catch {
    // Boîte déjà ouverte, geste expiré : rien à rattraper, l'interface se
    // resynchronise sur le mode courant.
    return 'unavailable'
  } finally {
    notify()
  }
}

/**
 * S'abonne aux changements de ce que l'installation permet.
 *
 * Le navigateur décide seul du moment où il juge la page installable, et
 * l'utilisateur peut installer depuis le menu du navigateur : l'interface ne
 * peut pas se contenter de lire le mode une fois, à sa construction.
 */
export function onInstallAvailabilityChange(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
